
import React, { useState, useEffect } from 'react';
import { safeSessionStorage } from '../utils/safeStorage';

interface ImageWithLoaderProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src' | 'onError'> {
  src?: string | Blob | null;
  onError?: () => void;
}

const SESSION_CACHE_KEY = 'dekko_loaded_media_cache';

const getSessionCache = (): Set<string> => {
  try {
    const cached = safeSessionStorage.getItem(SESSION_CACHE_KEY);
    return cached ? new Set(JSON.parse(cached)) : new Set();
  } catch (e) {
    return new Set();
  }
};

const addToSessionCache = (url: string) => {
  try {
    const cache = getSessionCache();
    const normalized = normalizeUrl(url);
    if (!normalized) return;
    cache.add(normalized);
    safeSessionStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(Array.from(cache)));
  } catch (e) { }
};

const normalizeUrl = (url: string): string | null => {
  if (!url) return null;
  // Strip Firebase tokens and other query params for consistent matching
  try {
    const urlObj = new URL(url);
    return urlObj.origin + urlObj.pathname;
  } catch (e) {
    return url.split('?')[0];
  }
};

const loadedImageCache = getSessionCache();

const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({ src, alt, className, onError, ...props }) => {
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof src === 'string') {
      const normalized = normalizeUrl(src);
      if (normalized && loadedImageCache.has(normalized)) {
        return false;
      }
    }
    return true;
  });
  const [displaySrc, setDisplaySrc] = useState<string | undefined>();

  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      setDisplaySrc(undefined);
      if (onError) onError();
      return;
    }

    let objectUrl: string | undefined;
    let urlToLoad: string;

    if (typeof src === 'string') {
      urlToLoad = src;
    } else if (src instanceof Blob) {
      objectUrl = URL.createObjectURL(src);
      urlToLoad = objectUrl;
    } else {
      setIsLoading(false);
      setDisplaySrc(undefined);
      if (onError) onError();
      return;
    }

    setIsLoading(true);
    setDisplaySrc(urlToLoad);

    const img = new Image();
    img.src = urlToLoad;

    const handleLoad = () => {
      if (typeof src === 'string') {
        addToSessionCache(src);
      }
      setIsLoading(false);
    };

    const handleError = () => {
      setIsLoading(false);
      if (onError) {
        onError();
      }
    };

    img.addEventListener('load', handleLoad);
    img.addEventListener('error', handleError);

    if (img.complete) {
      handleLoad();
    }

    return () => {
      img.removeEventListener('load', handleLoad);
      img.removeEventListener('error', handleError);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, onError]);

  if (isLoading) {
    return <div className={`shimmer-bg ${className}`} />;
  }

  // This component will still attempt to render the image, but the parent (Avatar)
  // will handle the error and swap this component out for a fallback.
  return (
    <img
      src={displaySrc}
      alt={alt}
      className={`fade-in ${className}`}
      {...props}
    />
  );
};

export default ImageWithLoader;
